import type { SearchEntity } from '@scayle/storefront-nuxt'
import { getFirstAttributeValue } from '@scayle/storefront-nuxt'
import { useRouteHelpers } from '~/composables/useRouteHelpers'
import { useRouter } from '#app/composables/router'
import {
  getSuggestionName,
  isCategorySuggestion,
  isNavigationItemSuggestion,
  isProductSuggestion,
} from '#storefront-search/utils'

import type {
  TrackingEvent,
  TrackingPayload,
  TrackSearchEventParams,
} from '~/types/tracking'
import { usePageState } from '~/composables'

const useSearchEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const router = useRouter()

  type GenderKeyword = 'men' | 'woman' | 'kids' | 'no gender'
  const genderKeywords: GenderKeyword[] = ['men', 'woman', 'kids']

  const searched_gender = (searchDestination: string) =>
    genderKeywords.find((gender) => searchDestination.includes(gender)) ||
    'no gender'

  const trackSearch = ({
    searchTerm,
    suggestion = '',
    searchAction,
    searchDestination = '',
    pagePayload,
  }: TrackSearchEventParams) => {
    track('view_search_results', {
      label: 'search page',
      search_term: searchTerm,
      search_term_completed: suggestion || '',
      action: searchAction,
      search_destination: searchDestination,
      search_gender: searched_gender(searchDestination),
      category: 'internal search',
      ...pagePayload,
    })
  }
  const {
    getSearchSuggestionPath,
    getProductDetailRoute,
    buildCategorySuggestionRoute,
    buildNavigationTreeItemRoute,
  } = useRouteHelpers()

  const { setSearchTermItem } = usePageState()

  return {
    trackSearch,
    trackSearchSuggestionClick: (
      searchTerm: string,
      suggestion: SearchEntity,
      searchCount: number,
    ) => {
      if (!suggestion.type) {
        return
      }

      const baseInfo = {
        searchTerm,
        suggestion: getSuggestionName(suggestion),
        searchDestination: getSearchSuggestionPath(suggestion) || '',
      } as const

      if (isProductSuggestion(suggestion)) {
        const { product } = suggestion.productSuggestion
        const route = getProductDetailRoute(
          product.id,
          getFirstAttributeValue(product.attributes, 'name')?.label,
          getFirstAttributeValue(product.attributes, 'color')?.label,
          getFirstAttributeValue(product.attributes, 'brand')?.label,
        )
        setSearchTermItem(
          route,
          JSON.stringify({
            ...baseInfo,
            searchAction: 'suggested_product',
            pagePayload: {
              search_results: searchCount,
              null_search: searchCount === 0,
            },
          }),
        )
        return
      }

      if (isCategorySuggestion(suggestion)) {
        const route = buildCategorySuggestionRoute(suggestion)
        setSearchTermItem(
          route.path,
          JSON.stringify({
            ...baseInfo,
            searchAction: 'suggested_category',
            pagePayload: {
              search_results: searchCount,
              null_search: searchCount === 0,
            },
          }),
        )
        return
      }

      if (isNavigationItemSuggestion(suggestion)) {
        const navigationItem =
          suggestion.navigationItemSuggestion.navigationItem
        const { route } = buildNavigationTreeItemRoute(navigationItem) ?? {}

        if (typeof route !== 'string' && route?.path) {
          const matched = router.resolve(route.path)
          setSearchTermItem(
            matched.path,
            JSON.stringify({
              ...baseInfo,
              searchAction: 'suggested_page',
              pagePayload: {
                search_results: searchCount,
                null_search: searchCount === 0,
              },
            }),
          )
          return
        }
      }

      trackSearch({
        ...baseInfo,
        searchAction: 'search_term',
      })
    },
  }
}

export default useSearchEvents
